import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, apiUpload } from './client'
import type { STECKBRIEF_FELDER } from '@/pages/grundartikel/feldhilfe'

export type SteckbriefFeld = typeof STECKBRIEF_FELDER[number]
export type BildQuelle = 'manuell' | 'shopify' | 'amazon' | 'ebay' | 'metro' | 'ki'
export type BildMeta = Partial<Record<SteckbriefFeld, { value: string | null; source: BildQuelle; at: string }>>
export type SteckbriefPatch = Partial<Record<SteckbriefFeld, string | null>>
export type BildRolle = 'haupt' | 'neben' | 'detail' | 'masse' | 'anwendung' | 'verpackung'
// GET src/modules/stock/routes.ts, B4: Images (explizite Projektion).
export interface Produktbild {
  id: number; url: string; position: number; alt_text: string | null; is_primary: boolean
  portal: string | null; created_at: string; role: BildRolle | null; portals: string[] | null
  variant_sku: string | null; source: 'url' | 'upload' | 'archiv'; file_hash: string | null
  seo_name: string | null; width: number | null; height: number | null
  public_url: string; beschreibung: string | null
}
// src/modules/product-knowledge/image-steckbrief.ts: BildSteckbrief.
export interface BildSteckbrief {
  file_hash: string; meta: BildMeta; width: number | null; height: number | null
  origin: string; seo_name: string | null; eignung: { amazon_hauptbild: boolean | null }
}
export interface BildUploadAntwort {
  id: number; file_hash: string; public_url: string; duplicate_archive: boolean
}
export type BildArchivAntwort = Omit<BildUploadAntwort, 'duplicate_archive'>
export interface BildArchivUebernahme {
  file_hash: string; role: BildRolle; portals?: string[]; variant_sku?: string
}
export interface BildUpload {
  file: File; role: BildRolle; portals: string[]; variant_sku: string; seo_name: string; beschreibung: string
}
export interface BildVerwendung {
  role?: BildRolle; portals?: string[] | null; variant_sku?: string | null
  position?: number; is_primary?: boolean
}
const bilderPfad = (sku: string) => `/stock/${encodeURIComponent(sku)}/images/`
const steckbriefPfad = (hash: string) => `/product-knowledge/images/${encodeURIComponent(hash)}/steckbrief/`

function invalidiereArtikel(qc: QueryClient, sku: string | undefined) {
  void qc.invalidateQueries({ queryKey: ['product-images'] })
  void qc.invalidateQueries({ queryKey: ['product-knowledge', sku] })
  void qc.invalidateQueries({ queryKey: ['stock', sku, 'pictures'] })
  void qc.invalidateQueries({ queryKey: ['product-knowledge-images', sku] })
}
function invalidiereBildMeta(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['product-images'] })
  // Archiv-Meta ist global: derselbe Hash kann mehreren Artikeln zugeordnet sein.
  void qc.invalidateQueries({ queryKey: ['produktbild-steckbrief'] })
  void qc.invalidateQueries({ queryKey: ['stock'], predicate: query => query.queryKey[2] === 'pictures' })
  void qc.invalidateQueries({ queryKey: ['product-knowledge-images'] })
}

// Vertrag: src/modules/product-knowledge/image-resolver.ts.
export interface ResolvedPhoto {
  publicUrl: string;
  fileHash: string; sourceSku: string; match: 'manual' | 'exact' | 'family'; variantSku: string | null
  usageId: number | null; seoName: string | null; altText: string | null; sourcePortals: string[]; origin: string
}
export interface ResolvedImages {
  limited?: boolean;
  sku: string; primary: ResolvedPhoto | null; gallery: ResolvedPhoto[]; sourceSku: string | null
  reason: 'no_source' | 'pending_ingest' | 'file_missing' | null
  components: (ResolvedImages & { quantity: number })[]
}
export interface ResolvedImageBatch { enabled: boolean; results: ResolvedImages[] }
export function useResolvedImages(skus: (string | null | undefined)[]) {
  const unique = [...new Set(skus.filter((sku): sku is string => !!sku))].sort()
  return useQuery({
    queryKey: ['product-images', unique],
    queryFn: () => api.post<ResolvedImageBatch>('/product-knowledge/images/resolve/', { skus: unique }),
    enabled: unique.length > 0, staleTime: 60_000,
  })
}
export function usePrimaryImage(sku: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['primary-image', sku],
    mutationFn: ({ photo, usageId }: { photo: ResolvedPhoto | null; usageId?: number }) => {
      const id = usageId ?? (photo?.sourceSku === sku ? photo.usageId : undefined)
      return id != null ? api.post(`${bilderPfad(sku)}${id}/`, { role: 'haupt', is_primary: true })
        : api.post(`${bilderPfad(sku)}from-archive/`, { file_hash: photo!.fileHash, role: 'haupt', ...(photo?.variantSku ? { variant_sku: photo.variantSku } : {}) })
    },
    onMutate: async ({ photo, usageId }) => {
      await qc.cancelQueries({ queryKey: ['product-images'] })
      await qc.cancelQueries({ queryKey: ['stock', sku, 'pictures'] })
      const snapshots = qc.getQueriesData<ResolvedImageBatch>({ queryKey: ['product-images'] })
      const pictures = qc.getQueryData<Produktbild[]>(['stock', sku, 'pictures'])
      for (const [key, batch] of snapshots) {
        if (!batch) continue
        qc.setQueryData(key, { ...batch, results: batch.results.map(row => row.sku === sku ? {
          ...row, primary: photo ? { ...photo, match: 'manual', sourceSku: sku } : null,
          sourceSku: sku, components: [], reason: photo ? null : 'pending_ingest',
        } : row) })
      }
      if (pictures) qc.setQueryData(['stock', sku, 'pictures'], pictures.map(p => ({ ...p,
        is_primary: p.id === (usageId ?? photo?.usageId) || !!photo && p.file_hash === photo.fileHash,
        role: p.id === (usageId ?? photo?.usageId) || !!photo && p.file_hash === photo.fileHash ? 'haupt' : p.role === 'haupt' ? 'neben' : p.role,
      })))
      return { snapshots, pictures }
    },
    onError: (_error, _variables, context) => {
      for (const [key, value] of context?.snapshots ?? []) qc.setQueryData<ResolvedImageBatch>(key, current => current && value ? {
        ...current, results: current.results.map(row => row.sku === sku ? value.results.find(before => before.sku === sku) ?? row : row),
      } : current)
      if (context?.pictures) qc.setQueryData(['stock', sku, 'pictures'], context.pictures)
    },
    onSuccess: () => { invalidiereArtikel(qc, sku); invalidiereBildMeta(qc) },
  })
}
export function useProduktbilder(sku: string | undefined) {
  return useQuery({
    queryKey: ['stock', sku, 'pictures'],
    queryFn: () => api.get<Produktbild[]>(bilderPfad(sku!)), enabled: !!sku,
  })
}
export function useProduktbildUpload(sku: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BildUpload) => {
      const form = new FormData()
      form.append('file', input.file)
      form.append('role', input.role)
      const portals = input.portals.map(value => value.trim()).filter(Boolean)
      if (portals.length) form.append('portals', portals.join(','))
      if (input.variant_sku.trim()) form.append('variant_sku', input.variant_sku.trim())
      const seo = input.seo_name.trim()
      if (/^[a-z0-9-]{3,80}$/.test(seo)) form.append('seo_name', seo)
      if (input.beschreibung.trim()) form.append('beschreibung', input.beschreibung.trim())
      return apiUpload<BildUploadAntwort>(`${bilderPfad(sku)}upload/`, form)
    },
    onSuccess: () => {
      invalidiereArtikel(qc, sku)
      invalidiereBildMeta(qc)
    },
  })
}
export function useBildVerwendung(sku: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: BildVerwendung }) =>
      api.post<{ id: number }>(`${bilderPfad(sku)}${id}/`, payload),
    onSuccess: () => invalidiereArtikel(qc, sku),
  })
}
export function useBildUebernehmen(sku: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BildArchivUebernahme) => api.post<BildArchivAntwort>(`${bilderPfad(sku)}from-archive/`, input),
    onSuccess: () => invalidiereBildMeta(qc),
  })
}
export function useBildEntfernen(sku: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<{ deleted: true }>(`${bilderPfad(sku)}${id}/`),
    onSuccess: () => invalidiereArtikel(qc, sku),
  })
}
export function useSteckbrief(fileHash: string | null | undefined) {
  return useQuery({
    queryKey: ['produktbild-steckbrief', fileHash],
    queryFn: () => api.get<BildSteckbrief>(steckbriefPfad(fileHash!)), enabled: !!fileHash,
  })
}
export function useSteckbriefSpeichern(fileHash: string | null | undefined) {
  const qc = useQueryClient()
  return useMutation({
    // Beim ersten Upload steht der Hash erst nach der Upload-Antwort fest.
    mutationFn: ({ felder, zielHash = fileHash ?? undefined }: { felder: SteckbriefPatch; zielHash?: string }) => {
      if (!zielHash) throw new Error('Steckbrief kann ohne Bildhash nicht gespeichert werden')
      return api.patch<BildSteckbrief>(steckbriefPfad(zielHash), felder)
    },
    onSuccess: () => invalidiereBildMeta(qc),
  })
}
